import { useForm } from 'react-hook-form';
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  DefaultContainer,
  Divider,
  Form,
  Icon,
  Step,
  Text,
} from 'components';

import { StepPropTypes } from '../../constants/propTypes';
import PaymentCreditCard from './paymentCreditCard';
import PaymentInvoice from './paymentInvoice';
import { Gray, Purple } from 'constants/colors';
import { validatePaymentProfile } from 'services/api';
import { setPaymentProfile } from 'store/modules/subscription/actions';
import { AUTH_TYPES } from 'constants/authTypesName';
import { parseDataToApi } from 'utils/parseDataToApi';

export default function Payment(props) {

  const {
    theme,
    onSubmit,
    onCancel,
    initialValues,
  } = props;

  const dispatch = useDispatch();
  const { financeInfo, basicInfo, solutionInfo } = useSelector((state) => state.subscription);
  const { expirationDate, confirmation } = financeInfo || {};

  const {
    formState,
    register,
    handleSubmit,
    watch,
    control,
    errors,
    setValue,
    trigger,
  } = useForm({
    mode: 'all',
    defaultValues: {
      ...initialValues,
      paymentType: initialValues?.paymentType || 'world_bank_slip',
    },
  });

  const [loadingCreateSubscription, setLoadingCreateSubscription] = useState(false);
  const [failValidatePaymentProfile, setFailValidatePaymentProfile] = useState(false);

  const { isValid } = formState;
  const paymentType = watch('paymentType');

  const buildSubscriptionData = ({ paymentMethod, creditCard } = {}) => {
    const validDiscountCoupon = confirmation?.coupon || '';
    const vehicle_camera = solutionInfo?.cameraTypes?.conteleCamCount || 0;
    const vehicle_camera_ia = solutionInfo?.cameraTypes?.conteleCamIaCount || 0;
    const camera_setup = (vehicle_camera + vehicle_camera_ia) || 0;
    const isAllTrackers = solutionInfo?.trackersBatch?.isAllTrackers ?? true;

    return parseDataToApi({
      ...basicInfo,
      contele_system: solutionInfo?.vehicleManager?.conteleSystemCount || 0,
      contele_tracker: solutionInfo?.vehicleManager?.conteleTrackerCount || 0,
      plug_and_play: solutionInfo?.quantityTypes?.trackerPlugAndPlayCount || 0,
      tracker_2g: solutionInfo?.quantityTypes?.tracker2GCount || 0,
      tracker_4g: (solutionInfo?.quantityTypes?.tracker4GCount || 0) + (solutionInfo?.quantityTypes?.trackerPlugAndPlayCount || 0),
      blocking_12_volts: solutionInfo?.blocker?.useBlocker ? parseInt(solutionInfo?.blocker?.twelveVoltsCount) : 0,
      blocking_24_volts: solutionInfo?.blocker?.useBlocker ? parseInt(solutionInfo?.blocker?.twentyFourVoltsCount) : 0,
      fine_auto_update: solutionInfo?.finesManager?.fineManager || false,
      vehicle_camera: vehicle_camera,
      vehicle_camera_ia: vehicle_camera_ia,
      discountCoupon: validDiscountCoupon,
      paymentMethod,
      creditCardInfo: creditCard,
      camera_setup: camera_setup,
      expirationDate: expirationDate?.expirationDate,
      consultantId: basicInfo?.gettingStarted?.seller_id,
      partial_shipment: !isAllTrackers,
      first_batch_quantity: isAllTrackers ? 0 : (solutionInfo?.trackersBatch?.trackersQuantity || 0),
    });
  };

  const onConfirmSubscription = async ({ paymentMethod, creditCard }) => {
    if (paymentMethod !== 'world_credit_card') {
      dispatch(setPaymentProfile(null));
      return;
    }

    const subscriptionData = buildSubscriptionData({ paymentMethod, creditCard });
    const tokenFromStorage = localStorage.getItem(AUTH_TYPES.STORAGE_AUTH_PARAM_NAME) || "";
    const authToken = JSON.parse(tokenFromStorage);

    const { vindi_customer_id, vindi_payment_profile_id } = await validatePaymentProfile({
      subscriptionData: {
        contele_customer: subscriptionData.contele_customer,
        credit_card_to_pay: subscriptionData.credit_card_to_pay,
      },
      authToken,
    });

    dispatch(setPaymentProfile({ vindi_customer_id, vindi_payment_profile_id }));
  };

  const submitPayment = async (creditCard) => {
    const capturedPaymentType = paymentType;
    setLoadingCreateSubscription(true);

    try {
      await onConfirmSubscription({
        paymentMethod: capturedPaymentType,
        creditCard: creditCard
      });

      setFailValidatePaymentProfile(false);
      handleSubmit((formValues) => onSubmit({
        ...formValues,
        paymentType: capturedPaymentType,
        creditCard: {
          cardNumber: creditCard.cardNumber,
          expirationDate: creditCard.expirationDate,
          cvv: creditCard.cvv,
          cardHolder: creditCard.cardHolder,
        },
      }))();

    } catch (err) {
      console.error(err);
      setFailValidatePaymentProfile(true);
    } finally {
      setLoadingCreateSubscription(false);
    };
  };

  const handleCreditCardInfo = (creditCard) => {
    handleSubmit(() => submitPayment(creditCard))();
  };

  const handleResetError = () => {
    setFailValidatePaymentProfile(false);
  };

  return (
    <Step
      headerTitle="Forma de pagamento"
    >
      <DefaultContainer>
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '20px',
        }}>
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '20px',
            backgroundColor: Gray[100],
            borderRadius: '5px',
            padding: '20px',
            border: `1px solid ${Gray[250]}`,
          }}>
            <Text
              color={Gray[500]}
              fontSize="16px"
              fontWeight="600"
            >
              Escolha a forma de pagamento:
            </Text>
            <Form
              onSubmit={handleSubmit(onSubmit)}
              style={{ padding: 0 }}
            >
              <div style={{ display: 'flex', gap: '12px' }}>
                <div
                  onClick={async () => {
                    setValue('paymentType', 'world_bank_slip');
                    await trigger('paymentType');
                    handleResetError();
                  }}
                  style={{
                    display: 'flex',
                    width: "100%",
                    height: "44px",
                    border: paymentType === "world_bank_slip" ? `1px solid ${Purple[500]}` : `1px solid ${Gray[250]}`,
                    backgroundColor: paymentType === "world_bank_slip" ? Purple[500] : Gray[100],
                    borderRadius: "5px",
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    gap: '8px',
                  }}
                >
                  <Text
                    color={paymentType === "world_bank_slip" ? Gray[100] : Gray[300]}
                    fontSize="14px"
                    fontWeight="500"
                  >
                    Boleto Bancário
                  </Text>
                  <Icon
                    icon="solidCircleCheck"
                    color={paymentType === "world_bank_slip" ? Purple[100] : Gray[100]}
                    style={{ width: '16px', height: '16px' }}
                  />
                </div>

                <div
                  onClick={async () => {
                    setValue('paymentType', 'world_credit_card');
                    await trigger('paymentType');
                    handleResetError();
                  }}
                  style={{
                    display: 'flex',
                    width: "100%",
                    height: "44px",
                    border: paymentType === "world_credit_card" ? `1px solid ${Purple[500]}` : `1px solid ${Gray[250]}`,
                    backgroundColor: paymentType === "world_credit_card" ? Purple[500] : Gray[100],
                    borderRadius: "5px",
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    gap: '8px',
                  }}
                >
                  <Text
                    color={paymentType === "world_credit_card" ? Gray[100] : Gray[300]}
                    fontSize="14px"
                    fontWeight="500"
                  >
                    Cartão de Crédito
                  </Text>
                  <Icon
                    icon="solidCircleCheck"
                    color={paymentType === "world_credit_card" ? Purple[100] : Gray[100]}
                    style={{ width: '16px', height: '16px' }}
                  />
                </div>

                <input
                  type="hidden"
                  name="paymentType"
                  ref={register({ required: true })}
                  value={paymentType}
                  control={control}
                />
              </div>
              {
                paymentType && <Divider
                  margin="24px 0 0 0"
                  width="1px"
                />
              }
            </Form>

            <div>
              {paymentType === 'world_bank_slip'
                && (
                  <PaymentInvoice
                    {...{
                      theme,
                      onSubmit: submitPayment,
                      onCancel,
                      isValid,
                      handleSubmit,
                    }}
                  />
                )}
              {paymentType === 'world_credit_card' && (
                <PaymentCreditCard
                  {...{
                    theme,
                    onCancel,
                    isValid,
                    control,
                    errors,
                    watch,
                    register,
                    handleFields: handleCreditCardInfo,
                    isLoading: loadingCreateSubscription,
                  }}
                />
              )}
              {failValidatePaymentProfile &&
                <div style={{ display: 'grid', rowGap: '18px', textAlign: 'center', padding: '25px' }}>
                  <label>Ocorreu um erro ao validar o cartão de crédito !</label>
                  <label>Verifique os dados do cartão e tente novamente.</label>
                </div>
              }
            </div>
          </div>
        </div>
      </DefaultContainer>
    </Step>
  );
}

Payment.propTypes = StepPropTypes;
